import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, Edit, Business } from '@mui/icons-material';
import { FC, useState } from 'react';

import { useAuth } from '@carcareers/hooks';
import { useJobseekerDeleteAssociationMutation, useJobseekerAssociationsQuery } from '@carcareers/store';
import { JobseekerAssociationModal } from '@carcareers/components';
import { JobseekerAssociation } from '@libs/models';

export const AssociationsCard: FC = () => {
  const { session } = useAuth();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editAssociation, setEditAssociation] = useState<JobseekerAssociation | null>(null);

  const [deleteAssociation, { isLoading: isDeleting }] = useJobseekerDeleteAssociationMutation();
  const { data: associations } = useJobseekerAssociationsQuery(session?.profile?.id ?? 0, {
    skip: !session?.profile?.id,
  });

  const handleOpenAdd = () => setIsAddOpen(true);
  const handleCloseAdd = () => setIsAddOpen(false);

  const handleEditAssociation = (association: JobseekerAssociation) => {
    setEditAssociation(association);
  };

  const handleCloseEdit = () => {
    setEditAssociation(null);
  };

  const handleDeleteAssociation = (associationId: number) => {
    const jobseekerId = session?.profile?.id ?? 0;
    deleteAssociation({ jobseekerId, associationId });
  };

  return (
    <>
      <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: '16px !important' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
              <Business sx={{ mr: 1 }} />
              Professional Associations
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              size="small"
              sx={{
                borderRadius: 2,
                height: 32,
                mt: { xs: 2, md: 0 },
                display: { xs: 'none', md: 'flex' },
              }}
              onClick={handleOpenAdd}
            >
              Add Association
            </Button>
          </Box>

          <List
            sx={{
              '& .MuiListItemSecondaryAction-root': {
                position: 'unset',
                top: '32px',
                transform: 'unset',
              },
            }}
          >
            {associations?.map((association, index) => (
              <Box key={association.id}>
                <ListItem
                  sx={{ px: 0 }}
                  alignItems="flex-start"
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        color="secondary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleEditAssociation(association)}
                      >
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="secondary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleDeleteAssociation(association.id)}
                      >
                        {isDeleting ? <CircularProgress size={18} color="secondary" /> : <Delete sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={association.name}
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            position: 'relative',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '1.5em',
                              background: 'linear-gradient(transparent, white)',
                              pointerEvents: 'none',
                            },
                          }}
                        >
                          {association.description}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < (associations?.length ?? 0) - 1 && <Divider />}
              </Box>
            ))}
          </List>

          <Button
            variant="outlined"
            startIcon={<Add />}
            size="small"
            sx={{
              borderRadius: 2,
              height: 32,
              width: '100%',
              mt: 2,
              display: { xs: 'flex', md: 'none' },
            }}
            onClick={handleOpenAdd}
          >
            Add Association
          </Button>
        </CardContent>
      </Card>

      <JobseekerAssociationModal type="add" open={isAddOpen} onClose={handleCloseAdd} />

      {editAssociation && (
        <JobseekerAssociationModal
          type="edit"
          open={!!editAssociation}
          onClose={handleCloseEdit}
          association={editAssociation}
        />
      )}
    </>
  );
}; 